import React, { useRef, useState, useLayoutEffect } from 'react';
import './flipRollingInteger.css';

export const FLIP_ROLLING_ANIMATION_MS = 1200;
export const FLIP_ROLLING_STAGGER_MS = 120;

function padScore(n: number, digitCount: number): string {
  return String(n).padStart(digitCount, '0');
}

const FlipDigit: React.FC<{
  oldDigit: string;
  newDigit: string;
  index: number;
  totalDigits: number;
  style?: React.CSSProperties;
}> = ({ oldDigit, newDigit, index, totalDigits, style }) => {
  const oldVal = parseInt(oldDigit, 10) || 0;
  const newVal = parseInt(newDigit, 10) || 0;
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  const delayMs = (totalDigits - 1 - index) * FLIP_ROLLING_STAGGER_MS;
  const durationMs = FLIP_ROLLING_ANIMATION_MS - delayMs;

  useLayoutEffect(() => {
    if (oldVal === newVal) return;
    if (ref.current) {
      ref.current.style.transition = `transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`;
    }
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, [oldVal, newVal, delayMs, durationMs]);

  return (
    <span className="flip-rolling-digit-window" style={style}>
      <div
        ref={ref}
        className="flip-rolling-digit-strip"
        style={{
          transform: `translateY(-${mounted ? newVal : oldVal}em)`,
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <div key={d} className="flip-rolling-digit-cell">
            {d}
          </div>
        ))}
      </div>
    </span>
  );
};

/**
 * Integer that animates from previous to current value (airport flip-board), same pattern as standings scores.
 */
export const FlipRollingInteger: React.FC<{
  from: number | undefined;
  to: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ from, to, className, style }) => {
  const digitCount =
    from !== undefined ? Math.max(String(from).length, String(to).length) : String(to).length;
  const fromStr = from !== undefined ? padScore(from, digitCount) : null;
  const toStr = padScore(to, digitCount);
  const isAnimating = fromStr !== null && fromStr !== toStr;

  if (!isAnimating) {
    return (
      <span className={className} style={style}>
        {to}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{ ...style, display: 'inline-flex', alignItems: 'center', gap: '0.05em' }}
    >
      {toStr.split('').map((char, i) => (
        <FlipDigit
          key={i}
          oldDigit={fromStr![i]}
          newDigit={char}
          index={i}
          totalDigits={toStr.length}
        />
      ))}
    </span>
  );
};
